import { liveStreamApi } from "@circle-react/api/liveStreamApi";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import {
  isPublicStream,
  roomTypeData,
} from "@circle-react/helpers/liveStreamRoomHelpers";
import { liveRoomQueries } from "@circle-react/lib/queries/liveRooms";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { useCurrentContact } from "@/react/hooks/useCurrentContact";

export const useLiveRoom = () => {
  const { currentUser } = usePunditUserContext();
  const { currentContact } = useCurrentContact();
  const { error, success } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const liveNowQuery = liveRoomQueries.liveNow();
  const { data } = useQuery(liveNowQuery);
  // liveNow is main-rooms-only server-side (LiveNowService); this is the client-side counterpart
  const room = data?.records?.find(record => !record.breakout_room_id);
  const handleNavigateToRoom = () => {
    if (!room) {
      return;
    }

    const canGuestAccessRegularRoom = Boolean(
      currentContact && room.current_contact_rsvped_to_event,
    );
    const canAccessRegularRoom = Boolean(
      currentUser || canGuestAccessRegularRoom,
    );

    const shouldNavigateToPublicRoom =
      isPublicStream(room) && !canAccessRegularRoom;

    void navigate({
      to: shouldNavigateToPublicRoom
        ? "/live/$roomSlug/public"
        : "/live/$roomSlug",
      params: { roomSlug: room.slug },
    });
  };
  const endStreamMutation = useMutation({
    mutationFn: () => liveStreamApi.endStream({ id: room?.id }),
    onSuccess: () => {
      success(t(`live_streams.room.${roomTypeData(room)}.end_success`));
      void queryClient.removeQueries({ queryKey: liveNowQuery.queryKey });
    },
    onError: () =>
      error(t(`live_streams.room.${roomTypeData(room)}.end_error`)),
  });
  const handleEndStream = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    // TODO: display confirmation box
    endStreamMutation.mutate();
  };

  return {
    room,
    canManage: room?.can_manage,
    handleNavigateToRoom,
    handleEndStream,
  };
};
